import LoadingIndicator from './LoadingIndicator';
import Close from '../assets/icons/Close';
import useAppTheme from '../styles/useAppTheme';

import {css} from '@emotion/react';
import React, {ReactNode, useEffect} from 'react';
import {createPortal} from 'react-dom';

let globalModalCounter = 0;

type Props = {
  children?: ReactNode;
  isLoading?: boolean;
  isOpen?: boolean;
  onCloseClick?: () => void;
};

/**
 * A reusable `Modal` component that renders a modal dialog with customizable content.
 *
 * @param children - The content to be displayed inside the modal.
 * @param isLoading - Indicates whether the modal is in a loading state.
 * @param isOpen - Controls the visibility of the modal.
 * @param onCloseClick - Callback function triggered when the close button is clicked.
 *
 * @remarks
 * - When the modal is open, it prevents scrolling on the body element by setting `overflow: hidden`.
 *   This is managed using a global counter (`globalModalCounter`) to ensure that the `overflow` style
 *   is only reset when all modals are closed.
 * - The modal is rendered using `ReactDOM.createPortal` to attach it to the `document.body`.
 * - Use this `Modal` component when you need a standalone modal with direct control over its props.
 *   If you require a global modal with state management and hooks, consider using `useModal` instead.
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={true}
 *   isLoading={false}
 *   onCloseClick={() => console.log('Modal closed')}
 * >
 *   <p>Modal Content</p>
 * </Modal>
 * ```
 */
const Modal = (props: Props) => {
  const {children, isLoading = false, isOpen = true, onCloseClick} = props;
  const {breakpoints, shadows, spacings} = useAppTheme();

  useEffect(() => {
    if (isOpen) {
      globalModalCounter++;
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (isOpen) globalModalCounter--;
      if (globalModalCounter === 0) document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return;

  const backgroundCss = css({
    inset: 0,
    position: 'fixed',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000 * 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const loadingIndicatorCss = css({
    display: isLoading ? 'block' : 'none',
  });

  const bodyCss = css({
    display: isLoading ? 'none' : 'flex',
    background: 'white',
    borderRadius: spacings.px8,
    padding: `${spacings.px40} ${spacings.px15} ${spacings.px15} ${spacings.px15}`,
    position: 'relative',
    maxHeight: '95%',
    margin: `${spacings.px20}`,
    maxWidth: spacings.px693,
    minWidth: spacings.px100,
    minHeight: spacings.px100,
    [breakpoints.sm]: {
      maxHeight: '90%',
      padding: spacings.px30,
      margin: `${spacings.px30}`,
    },
  });

  const closeButtonCss = css({
    height: spacings.px44,
    width: spacings.px44,
    lineHeight: spacings.px24,
    borderRadius: '100%',
    position: 'absolute',
    backgroundColor: 'white',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.sm]: {
      boxShadow: shadows.m,
      transform: 'translateX(50%) translateY(-50%)',
    },
  });

  const contentCss = css({
    display: 'flex',
    justifyContent: 'center',
    maxHeight: '100%',
    overflowY: 'auto',
    width: '100%',
  });

  const renderLoadingIndicator = () =>
    isLoading && (
      <div css={loadingIndicatorCss}>
        <LoadingIndicator />
      </div>
    );

  const modal = (
    <div css={backgroundCss}>
      {renderLoadingIndicator()}
      <div css={bodyCss}>
        <button css={closeButtonCss}>
          <Close
            css={{width: spacings.px24, height: spacings.px24}}
            onClick={onCloseClick}
          />
        </button>
        <div css={contentCss}>{children}</div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

export default Modal;
